/**
 * The SUP-191 checkpoint log — a git-style refs/objects split:
 *
 *  - a mutable **refs doc** (`CheckpointsRefDoc`, URL on `Project.checkpointsDocUrl`)
 *    holding one tiny `CheckpointRef` per checkpoint. It's an Automerge *list*,
 *    not a single tip pointer, on purpose: Automerge merges concurrent list
 *    appends, so two peers checkpointing at once both land — a scalar `{tip}`
 *    would clobber one (last-writer-wins). The list is the git "view".
 *  - one immutable **content doc** (`CheckpointContent`) per checkpoint holding
 *    the heavy per-file heads map + captured structure. Created once, never
 *    `change()`d again, so after its first sync it generates zero traffic and is
 *    fully evictable — that's what keeps checkpoint data off the hot path.
 *
 * Restore/inspect load a content doc lazily via its ref's `contentUrl`
 * (`vcs.ts:restoreCheckpoint`).
 *
 * The refs doc also carries `head`: the checkpoint the workspace currently
 * descends from. Capture parents to it and restore moves it, which is the whole
 * of what makes the flat list a tree. Those are the only two writers.
 */

import type { AutomergeUrl, Repo } from "@automerge/automerge-repo";

import { plainCreateDoc, type CreateDoc } from "./fs/dir-tree";
import type { PlayFileInode } from "./fs/types";
import { normalizeDirPath } from "./history/scope";
import type {
  CheckpointContent,
  CheckpointRef,
  CheckpointsDoc,
  CheckpointsRefDoc,
  Project,
  ProjectCheckpoint,
} from "./project";
import {
  captureCheckpointContent,
  generateCheckpointId,
  type HeadsReader,
  legacyCheckpointToContent,
  type PhaseRunner,
  restoreCheckpoint,
  type RestoreCheckpointResult,
  untimedPhase,
} from "./vcs";

/**
 * Ensure the project has a checkpoints refs doc, returning its URL. Mints one
 * (a single field on the project doc) if absent.
 *
 * Call ONLY from a single-writer context — Hearth's `ProjectDoc`, Bear's
 * `createProject` / server `GET /:projectId`, and migration. Two concurrent
 * minters would each create a rival refs doc and set the scalar
 * `checkpointsDocUrl`; Automerge resolves that to one URL and the loser's
 * checkpoints are orphaned. Clients must read `Project.checkpointsDocUrl`, never
 * mint it.
 */
export async function ensureCheckpointsDoc(
  repo: Repo,
  indexUrl: AutomergeUrl,
  createDoc: CreateDoc = plainCreateDoc(repo),
): Promise<AutomergeUrl> {
  const handle = await repo.find<Project>(indexUrl);
  await handle.whenReady();
  const existing = handle.doc()?.checkpointsDocUrl;
  if (existing) return existing;
  const refs = await createDoc<CheckpointsRefDoc>({ checkpoints: [] });
  await refs.whenReady();
  handle.change((doc) => {
    if (!doc.checkpointsDocUrl) doc.checkpointsDocUrl = refs.url;
  });
  return handle.doc()?.checkpointsDocUrl ?? refs.url;
}

/**
 * Create a checkpoint: write the immutable content doc and push a `CheckpointRef`
 * onto the refs doc. This is the **only** place a content doc is created.
 *
 * IMMUTABILITY CONTRACT — the content doc is written once here and never
 * `change()`d again. `repo.create(content)` collapses the whole payload into a
 * single change, so the doc has O(1) history and, after its first sync, produces
 * no further sync traffic and can be evicted freely. A stray `change()` on a
 * content doc reopens its history and defeats SUP-191 — never mutate one.
 */
export interface CheckpointInput {
  description: string;
  path: string;
  parent?: string;
  content: CheckpointContent;
  /** Preserve an existing id/createdAt (migration); minted fresh otherwise. */
  id?: string;
  createdAt?: number;
}

/**
 * Record a checkpoint the workspace has just ARRIVED at, so it becomes the
 * active one. Capturing is arriving: the files are in that state right now.
 */
export function createCheckpoint(
  repo: Repo,
  checkpointsDocUrl: AutomergeUrl,
  input: CheckpointInput,
  createDoc: CreateDoc = plainCreateDoc(repo),
  phase: PhaseRunner = untimedPhase,
): Promise<CheckpointRef> {
  return appendCheckpoint(
    repo,
    checkpointsDocUrl,
    input,
    true,
    createDoc,
    phase,
  );
}

/**
 * Record a checkpoint the workspace was at in the PAST, leaving the active one
 * alone. Importing history is not arriving at it, and moving HEAD here would
 * point the marker at a state the files are not in (the bug codex caught: a
 * bounded migration interleaved with a real capture reset HEAD backwards, and
 * the next capture then hung off the wrong branch).
 */
export function importCheckpoint(
  repo: Repo,
  checkpointsDocUrl: AutomergeUrl,
  input: CheckpointInput,
  createDoc: CreateDoc = plainCreateDoc(repo),
): Promise<CheckpointRef> {
  return appendCheckpoint(
    repo,
    checkpointsDocUrl,
    input,
    false,
    createDoc,
    untimedPhase,
  );
}

async function appendCheckpoint(
  repo: Repo,
  checkpointsDocUrl: AutomergeUrl,
  input: CheckpointInput,
  becomesActive: boolean,
  createDoc: CreateDoc,
  phase: PhaseRunner,
): Promise<CheckpointRef> {
  const contentHandle = await phase("persist", async (note) => {
    note({ "heads.count": Object.keys(input.content.heads).length });
    return createDoc<CheckpointContent>(input.content);
  });
  // Captured directories ride in `heads` too; they aren't files.
  const fileCount =
    Object.keys(input.content.heads).length -
    (input.content.directories?.length ?? 0);
  return phase("commit", () =>
    appendRef(
      repo,
      checkpointsDocUrl,
      { ...input, contentUrl: contentHandle.url, fileCount },
      becomesActive,
    ),
  );
}

/** A ref for a doc that already holds the checkpoint's data. */
interface RefInput {
  description: string;
  path: string;
  parent?: string;
  contentUrl: AutomergeUrl;
  fileCount: number;
  kind?: CheckpointRef["kind"];
  id?: string;
  createdAt?: number;
}

/**
 * Record a checkpoint whose immutable doc already exists, in the SUP-314
 * index-clone format (`history/capture.ts` made it). Publishing IS this
 * append: until the ref lands nothing lists or restores the clone. The card
 * hangs off HEAD and becomes HEAD, like any capture.
 */
export async function publishCloneCheckpoint(
  repo: Repo,
  checkpointsDocUrl: AutomergeUrl,
  input: {
    description: string;
    path: string;
    cloneUrl: AutomergeUrl;
    fileCount: number;
  },
): Promise<CheckpointRef> {
  const refsHandle = await repo.find<CheckpointsRefDoc>(checkpointsDocUrl);
  await refsHandle.whenReady();
  return appendRef(
    repo,
    checkpointsDocUrl,
    {
      description: input.description,
      path: input.path,
      parent: refsHandle.doc()?.head,
      contentUrl: input.cloneUrl,
      fileCount: input.fileCount,
      kind: "index-clone",
    },
    true,
  );
}

async function appendRef(
  repo: Repo,
  checkpointsDocUrl: AutomergeUrl,
  input: RefInput,
  becomesActive: boolean,
): Promise<CheckpointRef> {
  const refsHandle = await repo.find<CheckpointsRefDoc>(checkpointsDocUrl);
  await refsHandle.whenReady();

  // Read-then-change with nothing awaited between, so no concurrent create on
  // this peer can land between the stamp and the append.
  const ref: CheckpointRef = {
    id: input.id ?? generateCheckpointId(),
    description: input.description,
    createdAt: input.createdAt ?? nextCreatedAt(refsHandle.doc()?.checkpoints),
    path: normalizeDirPath(input.path),
    contentUrl: input.contentUrl,
    fileCount: input.fileCount,
    // Optional fields only when defined: Automerge rejects `undefined` values.
    ...(input.parent !== undefined ? { parent: input.parent } : {}),
    ...(input.kind !== undefined ? { kind: input.kind } : {}),
  };

  // One change, so no peer ever sees a card without the head that goes with it.
  refsHandle.change((doc) => {
    if (!doc.checkpoints) doc.checkpoints = [];
    doc.checkpoints.push(ref);
    if (becomesActive) doc.head = ref.id;
  });
  return ref;
}

/**
 * A timestamp strictly newer than every card already in the log, preferring the
 * local clock when it is already ahead. Two cards at the same instant then
 * can't exist on one peer, and a child is always stamped later than its parent
 * (a parent must be in the list to have been HEAD), so the rail can walk the
 * log in timestamp order and still meet every parent before its children.
 *
 * The list is the reference, not any clock, so a peer whose clock lags still
 * appends in order. Concurrent appends from DISCONNECTED peers can still tie,
 * which is why sibling order breaks ties on id rather than trusting this alone.
 */
function nextCreatedAt(existing: readonly CheckpointRef[] | undefined): number {
  const newest = (existing ?? []).reduce(
    (max, c) => Math.max(max, c.createdAt),
    0,
  );
  return Math.max(Date.now(), newest + 1);
}

export interface CaptureCheckpointOptions {
  /** Where the per-file heads come from. Defaults to walking `repo`; a
   *  storageless peer passes a reader aimed at the plane holding the docs. */
  readHeads?: HeadsReader;
  /** Creates the content doc; defaults to `repo.create` (`PlaygroundConfig.createDoc`). */
  createDoc?: CreateDoc;
  /** Runs each phase, for a host that times captures. */
  phase?: PhaseRunner;
}

/**
 * Capture the subtree at `path` (`"/"` = whole project) and create a checkpoint
 * for it.
 *
 * The new card hangs off HEAD, which is what makes the log a tree: capture
 * after going back to an older checkpoint and you branch from THERE, rather
 * than from whatever happens to be newest by the clock.
 */
export async function captureAndCreateCheckpoint(
  repo: Repo,
  checkpointsDocUrl: AutomergeUrl,
  files: Record<string, PlayFileInode>,
  path: string,
  description: string,
  opts: CaptureCheckpointOptions = {},
): Promise<CheckpointRef> {
  const phase = opts.phase ?? untimedPhase;
  const content = await captureCheckpointContent(
    repo,
    files,
    path,
    opts.readHeads,
    phase,
  );
  const refsHandle = await repo.find<CheckpointsRefDoc>(checkpointsDocUrl);
  await refsHandle.whenReady();
  return createCheckpoint(
    repo,
    checkpointsDocUrl,
    { description, path, parent: refsHandle.doc()?.head, content },
    opts.createDoc,
    phase,
  );
}

/**
 * Put the project back to `checkpoint` and move HEAD there.
 *
 * The two steps are one function because a restore that moved files without
 * moving HEAD would leave the rail lighting up a state the workspace is no
 * longer in. `message` is the change message stamped on each restored doc;
 * hosts encode their own author there.
 *
 * HEAD is written only after the restore returns, so a restore that throws
 * part-way leaves the marker on the branch the workspace is still on. It moves
 * only to a checkpoint the log actually contains: a not-yet-migrated legacy
 * entry is restorable but has no card, and pointing HEAD at an id nothing holds
 * would leave the next capture hanging off a parent that isn't there.
 *
 * This does NOT capture the state being left behind. Every checkpoint remains
 * reachable, so going back and forth between them is free, but edits made since
 * the last capture are discarded. That is what the confirm dialog says.
 */
export async function revertToCheckpoint(opts: {
  repo: Repo;
  indexUrl: AutomergeUrl;
  checkpointsDocUrl: AutomergeUrl;
  checkpoint: CheckpointRef | ProjectCheckpoint;
  message: string;
  /** Makes any directory a restore must recreate; defaults to `repo.create`. */
  createDoc?: CreateDoc;
}): Promise<RestoreCheckpointResult> {
  const { repo, indexUrl, checkpointsDocUrl, checkpoint, message } = opts;
  const result = await restoreCheckpoint(
    repo,
    indexUrl,
    checkpoint,
    message,
    opts.createDoc,
  );
  const refsHandle = await repo.find<CheckpointsRefDoc>(checkpointsDocUrl);
  await refsHandle.whenReady();
  refsHandle.change((doc) => {
    const known = doc.checkpoints?.some((c) => c.id === checkpoint.id);
    if (known) doc.head = checkpoint.id;
  });
  return result;
}

/**
 * Remove a checkpoint from the refs doc by id, keeping the tree connected: its
 * children are grafted onto its parent, and HEAD moves up if it pointed here.
 * A root's children become roots.
 *
 * Grafting rather than orphaning is what lets a future GC treat "unreachable
 * from a root" as garbage. The orphaned content doc is left for GC to archive +
 * reclaim (deleting it inline could race a concurrent restore).
 */
export async function deleteCheckpoint(
  repo: Repo,
  checkpointsDocUrl: AutomergeUrl,
  id: string,
): Promise<void> {
  const handle = await repo.find<CheckpointsRefDoc>(checkpointsDocUrl);
  await handle.whenReady();
  handle.change((doc) => {
    if (!doc.checkpoints) return;
    const index = doc.checkpoints.findIndex((c) => c.id === id);
    if (index === -1) return;
    const parent = doc.checkpoints[index]?.parent;
    doc.checkpoints.splice(index, 1);
    for (const c of doc.checkpoints) {
      if (c.parent !== id) continue;
      if (parent === undefined) delete c.parent;
      else c.parent = parent;
    }
    if (doc.head === id) {
      if (parent === undefined) delete doc.head;
      else doc.head = parent;
    }
  });
}

export interface MigrateCheckpointsResult {
  /** Legacy entries moved into refs+content docs this call. */
  migrated: number;
  /** Legacy entries still awaiting migration after this call. */
  remaining: number;
  /** True once every legacy entry is migrated and the inline/sidecar fields
   *  have been cleared from the project doc. */
  done: boolean;
}

/**
 * Migrate a project's pre-SUP-191 checkpoints — inline `Project.checkpoints`
 * plus any legacy `checkpointsUrl` sidecar — into the refs+content-doc model.
 *
 * Idempotent and resumable *without an external cursor*: the refs doc IS the
 * cursor — entries already present there (matched by id) are skipped, so a
 * re-run (or a crash mid-run) simply continues. `opts.limit` bounds the work
 * per call (each entry reconstructs `structure` by viewing the project doc at
 * historical heads, which is O(files)); call repeatedly until `done`.
 *
 * Only when nothing remains are the inline `checkpoints` + `checkpointsUrl`
 * fields deleted from the project doc — shrinking its state. (Its Automerge
 * *history* still retains them until a re-seed; that's a separate follow-up.)
 * The legacy sidecar *doc* is left for GC to reclaim.
 */
export async function migrateCheckpoints(
  repo: Repo,
  indexUrl: AutomergeUrl,
  opts?: { limit?: number },
): Promise<MigrateCheckpointsResult> {
  const limit = opts?.limit ?? Infinity;
  const checkpointsDocUrl = await ensureCheckpointsDoc(repo, indexUrl);

  const indexHandle = await repo.find<Project>(indexUrl);
  await indexHandle.whenReady();

  // Legacy entries: inline ∪ sidecar, dedup by id (inline wins), oldest first
  // so parents migrate before children if we ever thread parent ids.
  const legacyById = new Map<string, ProjectCheckpoint>();
  const doc = indexHandle.doc();
  const sidecarUrl = doc?.checkpointsUrl;
  if (sidecarUrl) {
    try {
      const sidecar = await repo.find<CheckpointsDoc>(sidecarUrl);
      await sidecar.whenReady();
      for (const c of sidecar.doc()?.checkpoints ?? []) legacyById.set(c.id, c);
    } catch {
      // Unloadable sidecar — nothing to migrate from it.
    }
  }
  for (const c of doc?.checkpoints ?? []) legacyById.set(c.id, c);
  const legacy = [...legacyById.values()].sort(
    (a, b) => a.createdAt - b.createdAt,
  );

  const refsHandle = await repo.find<CheckpointsRefDoc>(checkpointsDocUrl);
  await refsHandle.whenReady();
  const migratedIds = new Set(
    (refsHandle.doc()?.checkpoints ?? []).map((c) => c.id),
  );

  let migrated = 0;
  // Legacy entries carry no parent, so chain them oldest-first: a flat log was
  // a straight line, and N roots on the rail would be a worse lie than one.
  // Tracked over `legacy` rather than off HEAD so a resumed run picks up the
  // chain where it left off.
  let previousId: string | undefined;
  for (const cp of legacy) {
    if (migratedIds.has(cp.id)) {
      previousId = cp.id;
      continue;
    }
    if (migrated >= limit) break;
    const content = await legacyCheckpointToContent(repo, indexUrl, cp);
    await importCheckpoint(repo, checkpointsDocUrl, {
      id: cp.id,
      createdAt: cp.createdAt,
      description: cp.description,
      path: cp.path ?? "/",
      parent: previousId,
      content,
    });
    previousId = cp.id;
    migratedIds.add(cp.id);
    migrated++;
  }

  const remaining = legacy.filter((c) => !migratedIds.has(c.id)).length;
  const done = remaining === 0;
  if (done && (doc?.checkpoints || doc?.checkpointsUrl)) {
    indexHandle.change((d) => {
      delete d.checkpoints;
      delete d.checkpointsUrl;
    });
  }
  return { migrated, remaining, done };
}

/** True if `ancestor` is `dir` itself or a parent directory of `dir`. */
function isAncestorOrSelf(ancestor: string, dir: string): boolean {
  if (ancestor === "/") return true;
  if (dir === "/") return ancestor === "/";
  return dir === ancestor || dir.startsWith(`${ancestor}/`);
}

/**
 * Checkpoints whose scope is `path` or an ancestor directory of it, newest
 * first. A subdirectory therefore inherits every checkpoint taken on a parent
 * (or the project root) — those checkpoints already contain the subdir's state.
 *
 * Generic over the entry type so it reads both the SUP-191 refs doc
 * (`CheckpointRef`) and, during the migration window, a legacy project/sidecar
 * doc (`ProjectCheckpoint`) — both expose `path` + `createdAt`.
 */
export function listCheckpointsForDir<
  T extends { path: string; createdAt: number },
>(source: { checkpoints?: T[] } | undefined, path: string): T[] {
  const dir = normalizeDirPath(path);
  const list = source?.checkpoints ?? [];
  return list
    .filter((c) => isAncestorOrSelf(normalizeDirPath(c.path), dir))
    .sort((a, b) => b.createdAt - a.createdAt);
}
